import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authUtils } from '../../utils/auth.js';
import { useAuth } from '../../context/AuthContext.jsx';

/**
 * ProtectedRoute component that restricts access based on user roles
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles, 
  fallbackPath = '/unauthorized', 
  fallbackComponent = null 
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  // Composite auth state (context OR token present)
  const tokenData = authUtils.decodeToken();
  const cookieAuthenticated = authUtils.isAuthenticated();
  const authReady = isAuthenticated || cookieAuthenticated;

  // While checking, avoid false negatives
  if (isLoading && !cookieAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Checking permissions...
      </div>
    );
  }

  // Not authenticated at all → login
  if (!authReady) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Determine role from token, context or localStorage
  const userRoleRaw = tokenData?.role || tokenData?.userType || user?.userType || (authUtils.getCurrentUser()?.userType);
  const userRole = typeof userRoleRaw === 'string' ? userRoleRaw.trim() : userRoleRaw;

  // If authenticated but role not yet resolved, keep waiting instead of denying
  if (!userRole) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Preparing your dashboard...
      </div>
    );
  }

  // Normalize allowed roles (case-insensitive)
  const allowedRolesArray = (Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles])
    .filter(Boolean)
    .map(r => String(r).trim().toLowerCase());

  const hasAccess = allowedRolesArray.includes(String(userRole).trim().toLowerCase());

  if (!hasAccess) {
    if (fallbackComponent) return fallbackComponent;
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

/**
 * Hook to check if current user has specific role(s)
 * @param {string|string[]} roles - Role(s) to check
 * @returns {boolean} - True if user has any of the specified roles
 */
export const useRoleCheck = (roles) => {
  const { user } = useAuth();
  const tokenData = authUtils.decodeToken();
  const userRoleRaw = tokenData?.role || tokenData?.userType || user?.userType || (authUtils.getCurrentUser()?.userType);
  const userRole = typeof userRoleRaw === 'string' ? userRoleRaw.trim().toLowerCase() : String(userRoleRaw);
  
  const rolesArray = (Array.isArray(roles) ? roles : [roles])
    .filter(Boolean)
    .map(r => String(r).trim().toLowerCase());
  return rolesArray.includes(userRole);
};

/**
 * Component that renders children only if user has specific role(s)
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|string[]} props.roles - Role(s) required to see children
 * @param {React.ReactNode} props.fallback - Component to render if user doesn't have role
 */
export const RoleBasedRender = ({ children, roles, fallback = null }) => {
  const hasRole = useRoleCheck(roles);
  return hasRole ? children : fallback;
};

export default ProtectedRoute;

